/**
 * Skeleton loading placeholders - Premium Loading State System
 * Apple-inspired shimmer with reduced motion support
 * Performance optimized with React.memo
 */

import React, { memo } from 'react';
import { useReducedMotion } from '@/lib/hooks';

interface SkeletonProps {
  className?: string;
  /** Shimmer animation style */
  variant?: 'default' | 'shimmer' | 'pulse' | 'wave';
  /** Border radius preset */
  radius?: 'sm' | 'md' | 'lg' | 'xl' | 'full';
  /** Width preset for common sizes */
  width?: 'xs' | 'sm' | 'md' | 'lg' | 'full' | string;
  /** Height preset for common sizes */
  height?: 'xs' | 'sm' | 'md' | 'lg' | string;
}

const radiusMap = {
  sm: 'rounded-[8px]',
  md: 'rounded-[12px]',
  lg: 'rounded-[16px]',
  xl: 'rounded-[20px]',
  full: 'rounded-full',
};

const widthMap = {
  xs: 'w-12',
  sm: 'w-20',
  md: 'w-32',
  lg: 'w-48',
  full: 'w-full',
};

const heightMap = {
  xs: 'h-3',
  sm: 'h-4',
  md: 'h-5',
  lg: 'h-6',
};

export const Skeleton = memo(function Skeleton({
  className = '',
  variant = 'shimmer',
  radius = 'md',
  width,
  height,
}: SkeletonProps) {
  const prefersReducedMotion = useReducedMotion();

  // Use static background if user prefers reduced motion
  const baseClass = prefersReducedMotion
    ? 'bg-gray-100'
    : variant === 'shimmer'
      ? 'bg-gradient-to-r from-gray-100 via-gray-50 to-gray-100 bg-[length:200%_100%] animate-shimmer'
      : variant === 'wave'
        ? 'skeleton-wave'
        : variant === 'pulse'
          ? 'animate-pulse bg-gray-100'
          : 'animate-pulse bg-gray-200/60';

  const widthClass = width ? (widthMap[width as keyof typeof widthMap] || width) : '';
  const heightClass = height ? (heightMap[height as keyof typeof heightMap] || height) : '';

  return (
    <div
      className={`${baseClass} ${radiusMap[radius]} ${widthClass} ${heightClass} ${className}`}
      aria-hidden="true"
      role="presentation"
    />
  );
});

/**
 * Skeleton Text - For text placeholder with proper line height
 */
interface SkeletonTextProps {
  lines?: number;
  className?: string;
  /** Last line shorter for realistic text appearance */
  lastLineShorter?: boolean;
}

export const SkeletonText = memo(function SkeletonText({
  lines = 1,
  className = '',
  lastLineShorter = true,
}: SkeletonTextProps) {
  return (
    <div className={`space-y-2 ${className}`} aria-hidden="true" role="presentation">
      {Array.from({ length: lines }).map((_, i) => (
        <Skeleton
          key={i}
          height="sm"
          width={lastLineShorter && i === lines - 1 ? 'md' : 'full'}
          radius="sm"
        />
      ))}
    </div>
  );
});

/**
 * Skeleton Avatar - Circular placeholder for profile images
 */
interface SkeletonAvatarProps {
  size?: 'sm' | 'md' | 'lg' | 'xl';
  className?: string;
}

const avatarSizes = {
  sm: 'w-8 h-8',
  md: 'w-10 h-10',
  lg: 'w-12 h-12',
  xl: 'w-16 h-16',
};

export const SkeletonAvatar = memo(function SkeletonAvatar({
  size = 'md',
  className = '',
}: SkeletonAvatarProps) {
  return (
    <Skeleton
      className={`${avatarSizes[size]} ${className}`}
      radius="full"
    />
  );
});

/**
 * Skeleton Card - Full card placeholder with header and content
 */
interface SkeletonCardProps {
  /** Show header with avatar and title */
  hasHeader?: boolean;
  /** Number of content lines */
  contentLines?: number;
  className?: string;
}

export const SkeletonCard = memo(function SkeletonCard({
  hasHeader = true,
  contentLines = 3,
  className = '',
}: SkeletonCardProps) {
  return (
    <div
      className={`bg-white/96 rounded-[24px] p-5 shadow-card border border-white/60 ${className}`}
      aria-hidden="true"
      role="presentation"
    >
      {hasHeader && (
        <div className="flex items-center gap-3 mb-4">
          <SkeletonAvatar size="md" />
          <div className="flex-1 space-y-2">
            <Skeleton height="sm" width="lg" />
            <Skeleton height="xs" width="sm" />
          </div>
        </div>
      )}
      <SkeletonText lines={contentLines} />
    </div>
  );
});

/**
 * Inline Spinner - For button loading states
 */
interface SpinnerProps {
  size?: 'sm' | 'md' | 'lg';
  className?: string;
  /** Color class for the spinner */
  color?: string;
}

const spinnerSizes = {
  sm: 'w-4 h-4',
  md: 'w-5 h-5',
  lg: 'w-6 h-6',
};

export const Spinner = memo(function Spinner({
  size = 'md',
  className = '',
  color = 'text-current',
}: SpinnerProps) {
  const prefersReducedMotion = useReducedMotion();

  return (
    <svg
      className={`${spinnerSizes[size]} ${color} ${prefersReducedMotion ? '' : 'animate-spin'} ${className}`}
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
      role="presentation"
    >
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="3"
      />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
      />
    </svg>
  );
});

export const SkeletonTodayPage = memo(function SkeletonTodayPage() {
  return (
    <div className="space-y-5 pt-6 motion-safe:animate-fade-in px-1">
      {/* Hero skeleton - refined with stagger */}
      <div className="text-center space-y-3">
        <Skeleton className="h-4 w-28 mx-auto rounded-full" />
        <Skeleton className="h-9 w-4/5 mx-auto rounded-[14px]" />
        <Skeleton className="h-4 w-2/3 mx-auto rounded-full" />
      </div>

      {/* Check-in card skeleton - Premium glass style */}
      <div className="bg-white/96 rounded-[24px] p-5
                      shadow-[0_2px_8px_rgba(0,0,0,0.04),0_8px_24px_rgba(0,0,0,0.06)]
                      border border-white/60">
        <div className="flex items-center justify-between mb-4">
          <Skeleton className="h-5 w-28 rounded-[10px]" />
          <div className="flex gap-2">
            <Skeleton className="w-2.5 h-2.5 rounded-full" />
            <Skeleton className="w-2 h-2 rounded-full" />
            <Skeleton className="w-2 h-2 rounded-full" />
          </div>
        </div>
        <div className="space-y-5">
          {[1, 2, 3].map((i) => (
            <div key={i} className="space-y-2.5">
              <div className="flex justify-between">
                <Skeleton className="h-4 w-16 rounded-[8px]" />
                <Skeleton className="h-5 w-20 rounded-full" />
              </div>
              <div className="flex justify-between px-2">
                {[1, 2, 3, 4, 5].map((j) => (
                  <Skeleton key={j} className="w-3 h-3 rounded-full" />
                ))}
              </div>
            </div>
          ))}
        </div>
        <Skeleton className="h-[52px] w-full rounded-[16px] mt-6" />
      </div>

      {/* Plan card skeleton - Premium glass style */}
      <div className="bg-white/96 rounded-[24px] p-5
                      shadow-[0_2px_8px_rgba(0,0,0,0.04),0_8px_24px_rgba(0,0,0,0.06)]
                      border border-white/60">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2.5">
            <Skeleton className="h-5 w-24 rounded-[10px]" />
            <Skeleton className="h-5 w-10 rounded-full" />
          </div>
          <Skeleton className="h-4 w-16 rounded-[8px]" />
        </div>
        <Skeleton className="h-[3px] w-full rounded-full mb-4" />
        <div className="space-y-2.5">
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="bg-gray-50/50 rounded-[20px] p-3.5 flex items-center gap-3">
              <Skeleton className="w-[22px] h-[22px] rounded-full flex-shrink-0" />
              <div className="flex-1 space-y-1.5">
                <Skeleton className="h-4 w-4/5 rounded-[8px]" />
                <Skeleton className="h-3 w-16 rounded-[6px]" />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
});

/**
 * Empty state component with friendly, encouraging messaging
 * Premium Apple-inspired design with subtle animations
 */
interface EmptyStateProps {
  icon?: React.ReactNode;
  title: string;
  description: string;
  action?: React.ReactNode;
  variant?: 'default' | 'warm' | 'calm' | 'partner';
  /** Show decorative background elements */
  showDecoration?: boolean;
}

export const EmptyState = memo(function EmptyState({
  icon,
  title,
  description,
  action,
  variant = 'default',
  showDecoration = true,
}: EmptyStateProps) {
  const gradients = {
    default: 'from-primary-50 via-surface to-secondary-50',
    warm: 'from-secondary-50 via-surface to-primary-50',
    calm: 'from-primary-50 via-surface to-primary-100',
    partner: 'from-rose-50 via-surface to-pink-50',
  };

  const iconBg = {
    default: 'from-primary-100 to-primary-50',
    warm: 'from-secondary-100 to-secondary-50',
    calm: 'from-primary-100 to-primary-50',
    partner: 'from-rose-100 to-rose-50',
  };

  const glowColor = {
    default: 'rgba(0, 122, 255, 0.15)',
    warm: 'rgba(20, 184, 166, 0.15)',
    calm: 'rgba(0, 122, 255, 0.12)',
    partner: 'rgba(244, 63, 94, 0.15)',
  };

  return (
    <div className={`relative flex flex-col items-center justify-center py-14 px-6 text-center
                     motion-safe:animate-fade-in-up rounded-card
                     bg-gradient-to-br ${gradients[variant]}
                     border border-white/60
                     shadow-card
                     overflow-hidden`}>
      {/* Decorative background elements */}
      {showDecoration && (
        <>
          <div
            className="absolute -top-24 -right-24 w-48 h-48 rounded-full blur-3xl opacity-50"
            style={{ background: glowColor[variant] }}
            aria-hidden="true"
          />
          <div
            className="absolute -bottom-20 -left-20 w-40 h-40 rounded-full blur-3xl opacity-35"
            style={{ background: glowColor[variant] }}
            aria-hidden="true"
          />
        </>
      )}

      {icon && (
        <div className={`relative w-[72px] h-[72px] rounded-[22px]
                         bg-gradient-to-br ${iconBg[variant]}
                         flex items-center justify-center mb-5
                         shadow-[0_4px_12px_rgba(0,0,0,0.06),0_1px_3px_rgba(0,0,0,0.04)]
                         z-10`}>
          <div className="absolute inset-0 rounded-[22px] bg-white/40" />
          <div className="relative">
            {icon}
          </div>
        </div>
      )}
      <h3 className="relative text-title-sm font-semibold text-gray-900 mb-2 tracking-[-0.015em] z-10">
        {title}
      </h3>
      <p className="relative text-body text-gray-500/90 max-w-[280px] mb-6 leading-relaxed z-10">
        {description}
      </p>
      {action && <div className="relative z-10">{action}</div>}
    </div>
  );
});
